import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ENDED } from '@careapp-group/core';
import { ChatDto, ChatsService } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import { from, interval, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Chats, Members } from '../actions';
import { auth } from '../auth/auth.selectors';
import * as selectors from '../reducers';
import { CHATS } from '../reducers';

@Injectable()
export class ChatsEffects {
  createChat$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Chats.REQUEST_CREATE),
      withLatestFrom(this.store.pipe(select(selectors.getSelectedId(selectors.ORGANISATIONS)))),
      switchMap(([action, orgId]) =>
        this.chatsApi
          .createChat({ createChatRequestDto: { ...(<any>action).payload, organisationId: orgId } })
          .pipe(
            map((result) => Chats.ResponseSuccessCreateAction(result)),
            catchError((error) => of(Chats.ResponseFailCreateAction(error))),
          ),
      ),
    ),
  );

  endChat$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Chats.RequestEnd),
      switchMap((action) =>
        this.chatsApi
          .updateChat({ id: action.chatId, updateChatRequestDto: { status: ENDED } })
          .pipe(
            map((chat) => Chats.ResponseSuccessEnd({ payload: chat })),
            catchError((error) =>
              of(
                Chats.ResponseFailEnd({
                  id: action.chatId,
                  ...error,
                }),
              ),
            ),
          ),
      ),
    ),
  );

  // :TODO: (Trent Bowden): CA-1297 determine correct type, instead of 'any'
  joinChat$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Chats.RequestJoin),
      withLatestFrom(this.store.pipe(select(selectors.chats.getChatTokenMap))),
      switchMap(([action, chatTokens]) => {
        const chatId = action.payload;
        const existingTokenForChat = chatTokens[chatId];
        if (existingTokenForChat) {
          const decoded = jwtDecode(existingTokenForChat);
          const expiryDate = new Date(decoded['exp'] as number);
          const oneMinute = 1000 * 60;
          // Reuse existing token if it's not expiring soon
          if (Date.now() - expiryDate.valueOf() < oneMinute) {
            return of(
              Chats.ResponseSuccessJoin(<any>{
                chatId,
                externalToken: existingTokenForChat,
              }),
            );
          }
        }
        return this.chatsApi.joinChat({ id: chatId }).pipe(
          map((result) => Chats.ResponseSuccessJoin({ payload: result })),
          catchError((error) =>
            of(
              Chats.ResponseFailJoin({
                id: action.payload,
                error,
              }),
            ),
          ),
        );
      }),
    ),
  );

  // :TODO: (Trent Bowden): CA-1297 determine correct type, instead of 'any'
  listMyChats$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Chats.REQUEST_ALL),
      withLatestFrom(
        this.store.pipe(select(auth.getLoggedInUser)),
        this.store.pipe(select(selectors.getIds(CHATS))),
      ),
      switchMap(([action, user, currentChats]) =>
        this.chatsApi.listChats().pipe(
          switchMap((result: ChatDto[]): Observable<Action> => {
            const actions: any[] = [Chats.ResponseSuccessAllAction(result)];
            const existingIds = new Set(currentChats);
            let newChatId: string = null;

            // Determine if there are new chats available.
            const hasNewChats =
              result
                .filter((item) => item.owner !== user.id) // Don't need to know about your own chats you create
                .map((item) => item.id)
                .filter((id) => {
                  if (!existingIds.has(id)) {
                    newChatId = id;
                    return true;
                  }
                }).length > 0;

            // Trigger the snackbar if new items & user isn't already in the chat.
            if (hasNewChats && newChatId != null && !this.router.url.includes(newChatId)) {
              const newChat = result.find((chat) => chat.id === newChatId);
              actions.push(Chats.NotifyNewChatAvailable({ chat: newChat }));
            }
            return from(actions);
          }),
          catchError((error) => of(Chats.ResponseFailAllAction(error))),
        ),
      ),
    ),
  );

  // For any members in the participants lists that we don't have profiles for - request them
  fetchParticipants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Chats.RESPONSE_SUCCESS_ALL),
      withLatestFrom(this.store.pipe(select(selectors.getEntities(selectors.MEMBERS)))),
      switchMap(([action, existingStaff]) =>
        from(
          Array.from(
            new Set(
              ((<any>action).payload as ChatDto[]).reduce(
                (prev, next) => prev.concat(next.invitees),
                [] as string[],
              ),
            ),
          )
            // :TODO: (Trent Bowden): CA-1297 determine correct type, instead of 'any' * 2
            .filter((userId: any) => !existingStaff[userId])
            .map((id: any) => Members.RequestByIdAction({ id })),
        ),
      ),
    ),
  );

  joinChatOnCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Chats.RESPONSE_SUCCESS_CREATE),
      map((action) => Chats.JoinNow({ chatId: (<any>action).payload.id })),
    ),
  );

  pollForNew$ = createEffect(() =>
    this.store.pipe(
      select(auth.getLoggedInUser),
      distinctUntilChanged(),
      switchMap((user) => interval(environment.chatPollInterval).pipe(mapTo(user))),
      filter((user) => !!user),
      mapTo(Chats.RequestAllAction()),
    ),
  );

  navigateChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Chats.JoinNow),
        withLatestFrom(this.store.pipe(select(selectors.getSelected(selectors.ORGANISATIONS)))),
        map(([action, org]) => {
          this.router.navigate([
            `/`,
            org.tenantId,
            'organisations',
            org.id,
            'chats',
            action.chatId,
          ]);
          return action;
        }),
      ),
    // Important to not cause infinite loop
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private chatsApi: ChatsService,
    private store: Store<any>,
    private router: Router,
  ) {}
}
